export interface ServiceItem {
  name: string;
  description?: string;
  isImportant?: boolean;
}

export interface ServiceCategory {
  title: string;
  items: ServiceItem[];
}

export interface MilestoneRecommendation {
  core: ServiceCategory;
  fluids: ServiceCategory;
  wearAndTear: ServiceCategory;
  lessCommon: ServiceCategory;
  practicalAdvice: string[];
  costRange: {
    basic: string;
    withExtras: string;
    note?: string;
  };
  bluntTake: string[];
}

export const getMilestoneServices = (make: string, model: string, year: number, mileage: number): MilestoneRecommendation => {
  const normalizedMake = make.toLowerCase().trim();
  const normalizedModel = model.toLowerCase().replace(/[^a-z0-9]/g, '');

  // 1. SPECIFIC OVERRIDE: Lexus NX300h at 90k
  if (normalizedMake === 'lexus' && (normalizedModel === 'nx300h' || normalizedModel === 'nx300') && mileage === 90000) {
    return {
      core: {
        title: 'Core (almost always recommended)',
        items: [
          { name: 'Engine oil + oil filter change', isImportant: true },
          { name: 'Tire rotation + pressure check' },
          { name: 'Brake inspection (pads, rotors)' },
          { name: 'Cabin air filter replacement' },
          { name: 'Engine air filter replacement' },
          { name: 'Brake fluid replacement (if not done earlier)', isImportant: true },
          { name: 'Full multi-point inspection + road test' },
        ]
      },
      fluids: {
        title: 'Fluids & system refresh (important at this mileage)',
        items: [
          { name: 'Transmission fluid inspection or replacement', isImportant: true },
          { name: 'Coolant check (or replacement if nearing interval)' },
          { name: 'Hybrid system cooling inspection', isImportant: true },
          { name: 'Fuel system + emissions system check' }
        ]
      },
      wearAndTear: {
        title: 'Wear-and-tear items (condition-based)',
        items: [
          { name: 'Drive belts / serpentine belt inspection or replacement' },
          { name: 'Wiper blades' },
          { name: 'Battery + key fob battery' },
          { name: 'Brake pads (if worn)' },
          { name: 'Tires (alignment / replacement if needed)' }
        ]
      },
      lessCommon: {
        title: 'Less common but sometimes recommended',
        items: [
          { name: 'Spark plugs (often closer to 100K, but worth checking)' },
          { name: 'Timing components (inspection, not always replacement on NX hybrid)' },
          { name: 'Hybrid battery health check' }
        ]
      },
      practicalAdvice: [
        'If you’ve skipped earlier fluid services (30K / 60K) → do them now',
        'If your car is mostly city driving traffic → brakes and fluids matter more',
        'If you plan to keep the car past 150K → don’t skip transmission + brake fluid'
      ],
      costRange: {
        basic: '$400 – $700',
        withExtras: '$700 – $1,200+',
        note: 'Big variance depends on dealership vs independent shop.'
      },
      bluntTake: [
        'Given you’re analytical and long-term oriented:',
        '👉 Don’t just "check" fluids at 90K',
        '👉 Proactively replace brake fluid + evaluate transmission fluid seriously',
        'That one decision alone often separates smooth ownership to 150K+ vs annoying issues at 110K'
      ]
    };
  }

  // 2. SPECIFIC OVERRIDE: Acura MDX / RDX / TLX at 90k or 105k
  if (normalizedMake === 'acura' && (normalizedModel.includes('mdx') || normalizedModel.includes('rdx') || normalizedModel.includes('tlx')) && (mileage === 90000 || mileage === 100000 || mileage === 120000)) {
    return {
      core: {
        title: 'Core (Acura Maintenance Minder Schedule)',
        items: [
          { name: 'Engine oil + OEM filter change (Acura 0W-20 Full Synthetic)', isImportant: true },
          { name: 'Tire rotation & tire pressure calibration (Sub-code 1)' },
          { name: 'Front & rear brake inspection (pads, rotors, calipers)' },
          { name: 'Engine air filter + cabin pollen filter replacement (Sub-code 2)', isImportant: true },
          { name: 'Brake fluid flush & replacement (recommended every 3 years)', isImportant: true },
          { name: 'Multi-point inspection (suspension, steering, exhaust, boots)' },
        ]
      },
      fluids: {
        title: 'Acura Powertrain Fluids Refresh',
        items: [
          { name: 'Automatic transmission fluid exchange (Acura ATF Type 2.0 / 3.1 - Sub-code 3)', isImportant: true },
          { name: 'SH-AWD rear differential fluid replacement (Acura DPSF-II - Sub-code 6)', isImportant: true },
          { name: 'Transfer case fluid replacement (Acura Hypoid Gear Oil)', isImportant: true },
          { name: 'Engine coolant / antifreeze flush (Acura Long Life Type 2 - Sub-code 5)' }
        ]
      },
      wearAndTear: {
        title: 'Wear-and-tear items (condition-based)',
        items: [
          { name: 'Serpentine drive belt inspection or replacement' },
          { name: 'Wiper blades replacement' },
          { name: 'Battery health test & terminal cleaning' },
          { name: 'Brake pads & rotor resurfacing (if under 3mm)' },
          { name: 'Four-wheel alignment check' }
        ]
      },
      lessCommon: {
        title: 'Major Interval Service Items (90k-105k)',
        items: [
          { name: 'Spark plugs replacement (NGK Laser Iridium - Sub-code 4)', isImportant: true },
          { name: 'Timing belt + water pump replacement (on 3.5L V6 engines)', isImportant: true },
          { name: 'Valve clearance inspection & adjustment' },
          { name: 'PCV valve inspection' }
        ]
      },
      practicalAdvice: [
        'Acura SH-AWD rear differential fluid (DPSF-II) must never be skipped; failure causes clutch pack shudder and expensive differential failure.',
        'If your Acura has the 3.5L V6 engine (MDX, TLX V6), the timing belt, hydraulic tensioner, and water pump must be replaced between 90k–105k miles.',
        'Always insist on genuine Honda/Acura ATF fluid; universal transmission fluids frequently cause rough shifting in Acura transmissions.'
      ],
      costRange: {
        basic: '$450 – $750',
        withExtras: '$900 – $1,800+ (includes timing belt + SH-AWD fluids)',
        note: 'Independent Honda/Acura specialty shops typically charge 30-40% less than Acura dealerships.'
      },
      bluntTake: [
        'Acura vehicles are legendary for lasting 250k+ miles when properly maintained:',
        '👉 The #1 missed maintenance on MDX/RDX is the SH-AWD rear differential fluid. Service it now.',
        '👉 On 3.5L V6 engines, replace the timing belt and water pump promptly—it is an interference engine.',
        '👉 Independent Honda/Acura specialists use OEM parts without the dealership service premium.'
      ]
    };
  }

  // 2. GENERAL FALLBACK ENGINE
  const isMajorService = mileage % 30000 === 0;
  
  const rec: MilestoneRecommendation = {
    core: {
      title: 'Core (almost always recommended)',
      items: [
        { name: 'Engine oil + oil filter change', isImportant: true },
        { name: 'Tire rotation + pressure check' },
        { name: 'Brake inspection (pads, rotors, lines)' },
        { name: 'Multi-point inspection' }
      ]
    },
    fluids: {
      title: 'Fluids & system refresh',
      items: []
    },
    wearAndTear: {
      title: 'Wear-and-tear items (condition-based)',
      items: [
        { name: 'Wiper blades' },
        { name: 'Brake pads (if worn)' },
        { name: 'Tires (alignment / replacement if needed)' }
      ]
    },
    lessCommon: {
      title: 'Less common but sometimes recommended',
      items: []
    },
    practicalAdvice: [
      'Always refer to your specific owner\'s manual for the definitive schedule.',
    ],
    costRange: {
      basic: '$80 - $150',
      withExtras: '$150 - $400',
      note: 'Varies significantly based on labor rates and vehicle specifics.'
    },
    bluntTake: []
  };

  // Dynamically populate based on mileage and make
  if (mileage % 15000 === 0 || mileage % 20000 === 0) {
    rec.core.items.push({ name: 'Cabin Air Filter replacement' });
  }

  if (isMajorService) {
    rec.core.items.push({ name: 'Engine Air Filter replacement' });
    rec.fluids.items.push({ name: 'Brake fluid exchange', isImportant: true });
    
    if (mileage >= 60000) {
      rec.fluids.items.push({ name: 'Transmission fluid service', isImportant: true });
      rec.fluids.items.push({ name: 'Coolant/Antifreeze exchange' });
      rec.wearAndTear.items.push({ name: 'Drive belts / serpentine belt inspection or replacement' });
    }
    
    if (mileage >= 90000) {
      rec.lessCommon.items.push({ name: 'Spark plugs replacement (typically due 90k-120k)', isImportant: true });
    }

    rec.practicalAdvice.push(`At ${mileage.toLocaleString()} miles, fluids are degrading. Don't skip transmission or brake fluid services if you plan to keep the car.`);
    rec.costRange.basic = '$300 - $600';
    rec.costRange.withExtras = '$600 - $1000+';
    
    rec.bluntTake = [
      'This is a major service milestone.',
      '👉 Don\'t just do an oil change. Skipping major fluids now leads to expensive mechanical failures later.',
      '👉 Find a trusted independent mechanic; dealerships will heavily markup the fluid flushes.'
    ];
  } else {
    // Minor service
    rec.fluids.items.push({ name: 'Top off all essential fluids (washer, coolant, brake)' });
    rec.bluntTake = [
      'This is a minor service.',
      '👉 Stick to the basics: Oil and Rotation.',
      '👉 Don\'t let shops upsell you on fuel injector cleanings or "engine flushes" during a minor interval.'
    ];
  }

  // Brand specific generalizations
  if (['audi', 'vw', 'volkswagen', 'bmw', 'mercedes', 'porsche'].includes(normalizedMake)) {
    rec.practicalAdvice.push('European vehicles are sensitive to maintenance. Stick strictly to factory intervals and use OEM-approved fluids.');
    if (isMajorService) {
      rec.costRange.basic = '$400 - $800';
      rec.costRange.withExtras = '$800 - $1500+';
    }
  }

  if (['acura', 'honda'].includes(normalizedMake)) {
    rec.practicalAdvice.push('Acura/Honda vehicles utilize the Maintenance Minder system. On SH-AWD models, rear differential fluid (Acura DPSF-II) is essential to service every 30k miles.');
    if (isMajorService && mileage >= 60000) {
      rec.fluids.items.push({ name: 'Acura SH-AWD rear differential fluid (DPSF-II) check/replacement', isImportant: true });
      rec.fluids.items.push({ name: 'Transfer case fluid service' });
    }
    if (mileage >= 90000) {
      rec.lessCommon.items.push({ name: 'Timing belt + water pump inspection/replacement (for 3.5L V6 models)', isImportant: true });
      rec.lessCommon.items.push({ name: 'Valve clearance check' });
    }
  }

  return rec;
};
